import { tWithKeyAttr as t } from "../../i18nHelper";
import mainContentLayoutStyles from "../../components/mainContentLayout/mainContentLayout.module.css";
import ChatSideBar from "../../components/chatSidebar/chatSideBar";
import { mobileWidth } from "../../utils/constants";
import Sidebar from "../../components/sidebar/sidebar";
import React, { useEffect, useState } from "react";
import useMediaQuery from "../../hooks/useMediaQuery";
import Chat from "../../components/chat/chat";
import { Category, IConversation } from "../../types/types";
import { menuTitles } from "../../utils/categories";
import { useAppSelector } from "../../services/hooks";
import {
  callEndedSelector,
  chatConnectionSelector,
  chatStateSelector,
  chatUsersSelector,
  connectionErrorSelector,
  showAppointmentEndPopupSelector,
} from "../../services/selectors/chat.selector";
import Modal from "../../components/modals/modal/Modal";
import Chat_popup from "../../components/modals/chat_popup/chat_popup";
import { useActions } from "../../services/hooks/useActions";
import {
  useLazyGetAppointmentsByUserIdQuery,
} from "../../services/api/user.api";
import { userSelector } from "../../services/selectors/user.selectors";
import { videoCallInitialState } from "../../services/slices/chat.slice";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  useLazyInitializeGuestConnectionQuery,
} from "../../services/api/chat.api";
import i18n from "../../i18n";

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface IChatPageProps {
  activeCategory: Category;
  changeCategory: (e: React.MouseEvent, cat: Category) => void;
}

function ChatPage({ activeCategory, changeCategory }: IChatPageProps) {
  const { guid } = useParams();
  const user = useAppSelector(userSelector);
  const chatUsers = useAppSelector(chatUsersSelector);
  const connection = useAppSelector(chatConnectionSelector);
  const callEnded = useAppSelector(callEndedSelector);
  const chatState = useSelector(chatStateSelector);
  const connectionError = useAppSelector(connectionErrorSelector);
  const showAppointmentEndPopup = useAppSelector(
    showAppointmentEndPopupSelector
  );

  const {
    setCallAccepted,
    setCall,
    setStartCall,
    setCallEnded,
    setMyStream,
    setShowAppointmentEndPopup,
  } = useActions();

  const [therapist, setTherapist] = useState<IConversation | undefined>(
    undefined
  );
  const [openMenu, setOpenMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const mobile = useMediaQuery(mobileWidth);

  const [getAppointmentsByUserId] = useLazyGetAppointmentsByUserIdQuery();
  const [initGuestConnection] = useLazyInitializeGuestConnectionQuery();

  // Fetch appointments for the guest, then connect using the active appointment id.
  // The authenticated user flow is handled at the App level (App.tsx).
  useEffect(() => {
    if (!guid) return;
    if (!GUID_REGEX.test(guid)) return;
    if (connection) return; // already connected, don't reconnect

    getAppointmentsByUserId()
      .unwrap()
      .then((appointments) => {
        const active = appointments.find((a) => a.appointments.active);
        if (!active) {
          // handle no active appointment — show message to user
            console.error("No active appointment found for this link.");
          return;
        }
        initGuestConnection({ guid, appointmentId: active.appointments.id });
      })
      .catch((err) => console.error("Failed to fetch appointments for guest:", err));
  }, [guid]);

  useEffect(() => {
    if (connection && chatUsers?.length > 0) {
      if (guid) return; // guest — presence handled by UserConnected/UserDisconnected
      connection
        .invoke("FetchChatDataForUsers", chatUsers.map((u) => u.userId))
        .catch((err) => console.error("Error calling FetchChatDataForUsers:", err));
    }
  }, [connection, chatUsers.length]);
  useEffect(() => {
    if (!therapist?.userId) {
      setTherapist(
        chatUsers.find(
          (u) => u.appointment && u.appointment.appointments.active
        )
      );
    } else {
      setTherapist((prev) => chatUsers.find((u) => u.userId === prev?.userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatUsers]);

  const toggleMenu = () => setOpenMenu(!openMenu);

  const hideNotifications = () => {
    if (showNotifications) setShowNotifications(false);
  };

  const endCall = () => {
    setCall(videoCallInitialState);
    setCallAccepted(false);
    setStartCall(false);
    setCallEnded(undefined);
    if (chatState.myStream) {
      chatState.myStream.getTracks().forEach((track) => track.stop());
      setMyStream(undefined);
    }
    if (user.guid || guid) {
      getAppointmentsByUserId();
    }
  };

  const closeShowAppointmentEndPopup = () => {
    setShowAppointmentEndPopup(undefined);
    if (user.guid || guid) {
      getAppointmentsByUserId();
    }
  };

  return (
    <main
      className={`${mainContentLayoutStyles.main} ${mainContentLayoutStyles.main_chat
        } ${!openMenu && mainContentLayoutStyles.main_chat_small}`}
    >
      {connectionError && (
        <div
          className={mainContentLayoutStyles.connectionError}
        >
          {connectionError}
        </div>
      )}
      {/*<Notifications showNotifications={showNotifications} />*/}
      <div
        className={`${mainContentLayoutStyles.overlay} ${mobile && openMenu ? mainContentLayoutStyles.overlay_visible : ""
          }`}
      />
      <section
        dir={i18n.dir()}
        className={`${mainContentLayoutStyles.mainContent} ${mainContentLayoutStyles.mainContent_chat}`}
        onClick={hideNotifications}
      >
        <div
          className={`${mainContentLayoutStyles.header} ${mainContentLayoutStyles.header_chat}`}
        >
          <h1 className={`${mainContentLayoutStyles.mainContent__title}`}>
            {t(`${menuTitles[activeCategory]}`)}
          </h1>
          <button
            type="button"
            onClick={toggleMenu}
            className={mainContentLayoutStyles.burger}
          />
        </div>
        <Chat
          setTherapist={setTherapist}
          therapist={therapist}
          openMenu={openMenu}
        />
      </section>
      {!mobile && (
        <ChatSideBar
          setTherapist={setTherapist}
          therapist={therapist}
          openMenu={openMenu}
        />
      )}
      <Sidebar
        changeCategory={changeCategory}
        activeCategory={activeCategory}
        openMenu={openMenu}
        toggleMenu={toggleMenu}
      />
      {callEnded && (
        <Modal onClose={endCall}>
          <Chat_popup
            onClose={endCall}
            text={`השיחה הסתיימה על ידי ${callEnded}`}
          />
        </Modal>
      )}
      {showAppointmentEndPopup && (
        <Modal onClose={closeShowAppointmentEndPopup}>
          <Chat_popup
            onClose={closeShowAppointmentEndPopup}
            text={`הפגישה הסתיימה על ידי ${showAppointmentEndPopup}`}
          />
        </Modal>
      )}
    </main>
  );
}

export default ChatPage;
